import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  effect,
  inject,
  model,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { SpinnerService } from './core/shared/services/spinner.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthService } from './core/auth/auth.service';
import { UserService } from './core/shared/services/user.service';
import { distinctUntilChanged, filter, map, take } from 'rxjs';
import { SidenavDrawerComponent } from './core/shared/components/sidenav-drawer/sidenav-drawer.component';
import { SpinnerLoadComponent } from './core/shared/components/spinner-load/spinner-load.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { GlobalSidebarComponent } from './core/shared/components/global-sidebar/global-sidebar.component';
import { SidebarOpenSearchComponent } from './core/shared/components/sidebar-open-search/sidebar-open-search.component';
import { SearchType } from './core/shared/enums/search-type';
import { LessonsProjectService } from './core/shared/services/lessons-project.service';
import { TranslateModule } from '@ngx-translate/core';
import { ViewportService } from './core/shared/services/viewport.service';
import { SourcesDrawerComponent } from './core/shared/components/open-search/response-view/sources-drawer/sources-drawer.component';
import { DrawerSearchPanelService } from './core/shared/services/drawer-search-panel.service';
import { SearchPanelDrawerComponent } from './core/shared/components/open-search/search-panel-drawer/search-panel-drawer.component';
import { OpenSearchDrawerService } from './core/shared/services/open-search-drawer.service';
import { OpenSearchService } from './core/shared/services/open-search.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { FeedbackService } from './core/shared/services/feedback.service';
import { MatDialog } from '@angular/material/dialog';
import { FeedbackComponent } from './core/shared/components/dialogs/feedback/feedback.component';
import { OnBoardingService } from './core/shared/services/on-boarding.service';
import { ACTIVE_CATALOGUE_TAB_KEY } from './core/shared/constants/lessons-learning-sections.constants';
import { ReleaseOnboardingComponent } from './core/shared/components/release-onboarding/release-onboarding.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatProgressSpinnerModule,
    SidenavDrawerComponent,
    SpinnerLoadComponent,
    MatIconModule,
    MatTooltipModule,
    MatDividerModule,
    GlobalSidebarComponent,
    SidebarOpenSearchComponent,
    TranslateModule,
    SourcesDrawerComponent,
    SearchPanelDrawerComponent,
    ReleaseOnboardingComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  title = 'kp_frontend';
  spinnerService = inject(SpinnerService);
  private router = inject(Router);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private projectsService = inject(LessonsProjectService);
  private viewportService = inject(ViewportService);
  private openSearchService = inject(OpenSearchService);
  private feedbackService = inject(FeedbackService);
  private onBoardingService = inject(OnBoardingService);
  private _dialog_feedback = inject(MatDialog);
  drawer = inject(DrawerSearchPanelService);
  drawerService = inject(OpenSearchDrawerService);
  private route = inject(ActivatedRoute);
  private readonly homeOnboardingKey = 'Home-onboarding';
  isLengthProjects = this.projectsService.isLengthProjects;
  isAuthenticated = false;
  user = computed(() => this.userService.user());
  error = computed(() => this.userService.error());
  hasPermission = computed(() => this.userService.hasPermission());
  isLoadingProjects = this.projectsService.isLoadingProjects;
  noProjectsAvailable = computed(
    () => !this.isLoadingProjects() && !this.projectsService.error404() && !this.isLengthProjects(),
  );
  searchType = signal<SearchType>(SearchType.DEFAULT);
  showAllSearchResults = signal(false);
  loaded = signal(false);
  showOnlyNewSearch = model<boolean>(false);
  errorDrawer = computed(() => this.openSearchService.errorDrawer());
  spinnerConversational = computed(() => this.openSearchService.spinnerConversational());
  historyResponse = computed(() => this.openSearchService.historyResponse());
  hasAnySources = computed(() => {
    const history = this.openSearchService.historyResponse();
    const interactions = history?.openQueryInteractions ?? [];
    const hasResponseSources = interactions.some(
      i => i.response !== null && (i.sources?.length ?? 0) > 0,
    );
    if (hasResponseSources) return true;

    if (history?.modeType?.toLowerCase() !== 'lessons') return false;
    return (history.modeResponse?.lessons ?? []).some(lesson => {
      const hasValue = (value: unknown) =>
        Array.isArray(value)
          ? value.some(item => typeof item === 'string' && item.trim())
          : typeof value === 'string' && value.trim();
      return (
        hasValue(lesson.url) ||
        hasValue(lesson.file_name) ||
        hasValue(lesson.fileName) ||
        (Array.isArray(lesson.sources) && lesson.sources.some(source => !!source?.url))
      );
    });
  });
  private currentUrl = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );
  isHome = computed(() => this.currentUrl().includes('/home'));
  releaseOnboardingReady = computed(
    () =>
      this.isHome() &&
      this.loaded() &&
      !!this.user() &&
      this.hasPermission() === true &&
      !this.spinnerService.loading() &&
      this.spinnerService.elementsReady() &&
      !this.isLoadingProjects(),
  );
  isSearchOpen = computed(() => this.currentUrl().includes('isOpenSideBarSearch=true'));
  isLessonsRoute = computed(() =>
    /^\/(lessons-learning|lessons-view)(\/|$)/.test(this.currentUrl()),
  );
  isSourcesDrawerOpen = computed(
    () => this.drawerService.isOpen() && this.hasAnySources() && !this.spinnerConversational(),
  );
  isDrawerOpen = computed(() => this.drawer.state().open || this.isSourcesDrawerOpen());
  drawerWidth = computed(() => {
    const searchDrawerWidth = this.drawer.state().open ? this.drawer.width() : 0;
    const sourcesDrawerWidth = this.isSourcesDrawerOpen() ? this.drawerService.rightWidth() : 0;
    return Math.max(searchDrawerWidth, sourcesDrawerWidth);
  });
  private operationId = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => {
        let route = this.route.root;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route.snapshot.paramMap.get('id');
      }),
      distinctUntilChanged(),
    ),
    {
      initialValue: (() => {
        let route = this.route.root;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route.snapshot.paramMap.get('id');
      })(),
    },
  );

  isCrossCutting = computed(() => this.operationId() === 'Cross-cutting');

  constructor() {
    this.effectLoadingProjects();
    effect(() => {
      window.dispatchEvent(
        new CustomEvent('userLoaded', {
          detail: this.userService.user(),
        }),
      );
    });
    effect(() => {
      if (this.feedbackService._showCsat) {
        if (this.shouldWaitForHomeOnboarding() || this.onBoardingService.isTourActive()) {
          return;
        }
        this.feedbackService._showCsat = false;
        this.openDialog(true);
      }
    });
  }

  private shouldWaitForHomeOnboarding(): boolean {
    this.onBoardingService.tourCompletedVersion();
    if (!this.isHome() || this.isLoadingProjects()) return false;

    const onboardingSkipped =
      localStorage.getItem('isSkippedOnboarding') ||
      localStorage.getItem('hiddenTemporalOnboarding');

    return !onboardingSkipped && !this.onBoardingService.hasCompleted(this.homeOnboardingKey);
  }

  effectLoadingProjects() {
    effect(() => {
      if (this.isHome()) {
        sessionStorage.removeItem(ACTIVE_CATALOGUE_TAB_KEY);
      }
    });
    untracked(() => this.projectsService.fetchProjects());
  }

  ngOnInit(): void {
    const token = this.authService.getCurrentToken();
    if (token) {
      this.authService.msalInit(() => {
        this.authService.ensureActiveAccountIsSet();
        this.authService.checkAndSetActiveAccount(this.completeInit);
        this.authService.startTokenAutoRefresh();
      });
    } else {
      this.authService.msalInit(this.completeInit);
      this.authService.startTokenAutoRefresh();
    }
    this.viewportService.adjustRootFontSize();
    setTimeout(() => {
      this.loaded.set(true);
    }, 1000);
  }

  completeInit = () => {
    this.authService.isAuthenticated$
      .pipe(
        filter(isAuthenticated => isAuthenticated === true),
        take(1),
      )
      .subscribe(() => this.userService.fetchUser());
  };

  closeOpenSearch = () => {
    this.drawer.close();
    this.drawerService.close();
    void this.router.navigate([], {
      queryParams: { isOpenSideBarSearch: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  };

  openSearch(type: SearchType): void {
    this.drawer.close();
    this.drawerService.close();
    this.searchType.set(type);
    const isRecentSearch = type === SearchType.RECENT;
    this.showAllSearchResults.set(isRecentSearch);

    this.openSearchService.historyResponse.set(null);
    this.openSearchService.conversationIdSignal.set(null);
    this.openSearchService._dataOpenLogic.set(null);
    localStorage.removeItem('OpenSearchPath');
    localStorage.removeItem('lastOpenSearchPath');
    localStorage.removeItem('responseView');

    if (isRecentSearch) {
      this.openSearchService.getRecentSeaeches();
    }
    void this.router.navigate([], {
      queryParams: { isOpenSideBarSearch: 'true' },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  openDialog(isCsat = false) {
    this._dialog_feedback.open(FeedbackComponent, {
      width: isCsat ? '700px' : '400px',
      data: { content: 'Content here!' },
    });
  }

  transversalLesson() {
    this.drawerService.close();
    void this.router.navigate([`/lessons-learning/Cross-cutting`]);
  }
}
